import request from '@utils/request';
import moment from 'moment';
import { parseDateToString } from '@utils/helpers';
import {
  fetchEntity,
  fetchEntitySingle,
  fetchPaginatedEntity,
  updateEntitySingle,
  createEntitySingle,
  fetchEntityRequest,
  fetchEntitySuccess,
  fetchEntityFailure,
  fetchEntityPerDay,
} from './base.actions';

const ITEMS_PER_REQUEST = 20;

export const fetchProperties = () =>
  fetchEntity('properties', { url: 'properties', params: { perPage: 0 } });

export const fetchProperty = (propertyId) =>
  fetchEntitySingle('properties', { url: `properties/${propertyId}` });

export const fetchPaginatedProperties = (params) =>
  fetchPaginatedEntity('properties', { url: 'properties', params: { ...params, fallback: 1 } });

export const updateProperty = (id, data, section) =>
  updateEntitySingle('properties', {
    url: `properties/${id}${section && `?section=${section}`}`,
    data,
  });

export const createProperty = (data) =>
  createEntitySingle('properties', { url: 'properties', data });

export const fetchHomepage = (propertyId, language) =>
  fetchEntitySingle('homepages', {
    url: `${propertyId}/homepage/${language}`,
  });

export const fetchCategories = (propertyId, language) =>
  fetchEntity('categories', {
    url: `${propertyId}/categories/${language}`,
    params: { perPage: 0, fallback: 1 },
  });

export const fetchPaginatedCategories = (propertyId, params) =>
  fetchPaginatedEntity('categories', {
    url: `${propertyId}/categories`,
    params: { ...params, fallback: 1 },
  });

export const fetchCategory = (propertyId, categoryId, language, { isOptional = true } = {}) =>
  fetchEntitySingle('categories', {
    url: `${propertyId}/categories/${categoryId}/${language}`,
    params: { resources: 1 },
    isOptional,
  });

export const fetchCategoryProducts = (propertyId, categoryId, language, params) =>
  fetchPaginatedEntity('products', {
    url: `${propertyId}/categories/${categoryId}/products/${language}`,
    params: { ...params, fallback: 1 },
  });

export const fetchSpaCategories = (propertyId, language) =>
  fetchEntity('categories', {
    url: `${propertyId}/categories/${language}`,
    params: { perPage: 0 },
  });

export const fetchProducts = (propertyId, language) =>
  fetchEntity('products', {
    url: `${propertyId}/products/${language}`,
    params: { perPage: 0, fallback: 1 },
  });
export const fetchPaginatedProducts = (propertyId, params) =>
  fetchPaginatedEntity('products', {
    url: `${propertyId}/products`,
    params: { ...params, fallback: 1 },
  });
export const fetchProduct = (propertyId, productId, language) =>
  fetchEntitySingle('products', {
    url: `${propertyId}/products/${productId}/${language}`,
    isOptional: false,
  });

export const fetchUser = (userId) => fetchEntitySingle('users', { url: `auth/users/${userId}` });

export const fetchTermsAndConditionsPage = (propertyId, language) =>
  fetchEntitySingle('pages', { url: `${propertyId}/pages/terms-and-conditions/${language}` });
export const fetchPrivacyAndCookiesPage = (propertyId, language) =>
  fetchEntitySingle('pages', { url: `${propertyId}/pages/privacy-and-cookies/${language}` });
export const fetchCancellationPage = (propertyId, language) =>
  fetchEntitySingle('pages', { url: `${propertyId}/pages/cancellation/${language}` });
export const fetchFacilitiesPage = (propertyId, language) =>
  fetchEntitySingle('pages', { url: `${propertyId}/pages/facilities/${language}` });
export const fetchSpaDaysPage = (propertyId, language) =>
  fetchEntitySingle('pages', { url: `${propertyId}/pages/spa-days/${language}` });
export const fetchSpaPage = (propertyId, language) =>
  fetchEntitySingle('pages', { url: `${propertyId}/pages/spa/${language}` });

export const fetchTablesPage = (propertyId, language) =>
  fetchEntitySingle('pages', { url: `${propertyId}/pages/tables/${language}` });

export const fetchGolfPage = (propertyId, language) =>
  fetchEntitySingle('pages', { url: `${propertyId}/pages/golf/${language}` });

export const fetchGolfPayAndPlayPage = (propertyId, language) =>
  fetchEntitySingle('pages', { url: `${propertyId}/pages/golf-pay-and-play/${language}` });

export const fetchSplitAvailabilities =
  (entityName, getUrl, isOverwrite = false) =>
  ({ arrivalDate, date, categoryId, itemsCount, propertyId, language }) =>
  (dispatch) => {
    dispatch(fetchEntityRequest(entityName));
    const dateParam = entityName === 'spaPackages' ? { date } : { arrivalDate };
    const url = getUrl({ propertyId, language });
    const requestAmount = Math.ceil(itemsCount / ITEMS_PER_REQUEST);
    const requests = new Array(requestAmount).fill(0).map((_, index) =>
      request.get(url, {
        params: {
          category: categoryId,
          perPage: ITEMS_PER_REQUEST,
          ...dateParam,
          page: index + 1,
        },
      })
    );
    return Promise.all(requests)
      .then((requestResults) => {
        const mergedData = requestResults.reduce(
          (prev, current) => [...prev, ...current.data.data],
          []
        );
        dispatch(fetchEntitySuccess(entityName, { data: mergedData, isOverwrite }));
      })
      .catch(() => dispatch(fetchEntityFailure(entityName)));
  };

export const fetchFacilityTimeslot = (propertyId, spaTreatmentId, language, arrivalDate) =>
  fetchEntitySingle('facilities', {
    url: `${propertyId}/facilities/${spaTreatmentId}/${language}`,
    params: {
      arrivalDate: moment(arrivalDate).format(),
      isTimeSlot: 1,
    },
    isOptional: true,
  });

export const fetchFacility = (propertyId, facilityId, language, arrivalDate) =>
  fetchEntityPerDay('facilities', {
    url: `${propertyId}/facilities/${facilityId}/${language}`,
    params: {
      arrivalDate: parseDateToString(arrivalDate),
    },
    isOptional: false,
  });

export const fetchPackagings = (propertyId) =>
  fetchEntity('packagings', {
    url: `${propertyId}/packaging`,
  });

export const fetchPostages = (propertyId, language) =>
  fetchEntity('postages', {
    url: `${propertyId}/postage/${language}`,
  });
